// src/components/js/ProteinGroupComponent.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../css/ProteinGroupComponent.css';
import {
  FaSearch,
  FaArrowLeft,
  FaArrowRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaTimes,
  FaCopy,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ProteinGroupComponent = () => {
  const [proteins, setProteins] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedProtein, setSelectedProtein] = useState(null);
  const [fasta, setFasta] = useState('');
  const [modalType, setModalType] = useState(''); // 'fasta', 'title', 'accession'
  const [copied, setCopied] = useState(false);

  const limit = 20;
  const navigate = useNavigate();

  // Fetch proteins from local API
  useEffect(() => {
    const fetchProteins = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`http://197.156.76.122/api/protein-uniprot/?page=${page}`);
        setProteins(res.data.results);
        setTotal(res.data.count);
      } catch (error) {
        console.error('Error fetching proteins:', error);
      }
      setLoading(false);
    };
    fetchProteins();
  }, [page]);

  // Fetch FASTA from database
  const fetchFastaFromDB = (protein) => {
    if (protein.sequence) {
      const fastaData = `>${protein.accession} ${protein.full_name || protein.name}\n${protein.sequence}`;
      setFasta(fastaData);
    } else {
      setFasta(`>${protein.accession}\nFASTA sequence not available in database.`);
    }
    setModalType('fasta');
    setSelectedProtein(protein);
  };

  const handleShowModal = (protein, type) => {
    setSelectedProtein(protein);
    setModalType(type);
    if (type === 'fasta') fetchFastaFromDB(protein);
  };

  const handleBlastClick = (protein) => {
    navigate(`/blast?sequence=${protein.accession}`);
  };

  const handleCopy = () => {
    let textToCopy = '';
    if (modalType === 'fasta') textToCopy = fasta;
    else if (modalType === 'title') textToCopy = selectedProtein?.full_name || selectedProtein?.name;
    else if (modalType === 'accession') textToCopy = selectedProtein?.accession;

    navigator.clipboard.writeText(textToCopy || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalPages = Math.ceil(total / limit);

  const PaginationControls = () => (
    <div className="pagination-controls">
      <button onClick={() => setPage(1)} disabled={page === 1}><FaAngleDoubleLeft /> First</button>
      <button onClick={() => setPage(prev => prev - 1)} disabled={page === 1}><FaArrowLeft /> Prev</button>
      <span className="pagination-status">Page {page} of {totalPages}</span>
      <button onClick={() => setPage(prev => prev + 1)} disabled={page === totalPages}>Next <FaArrowRight /></button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>Last <FaAngleDoubleRight /></button>
    </div>
  );

  const getUniProtDetailLink = (protein) => {
    return `https://www.uniprot.org/uniprot/${protein.accession}`;
  };

  return (
    <div className="protein-group-container">
      <h2>Identical Protein Groups</h2>
      <p className="results-summary">
        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} proteins
      </p>

      <PaginationControls />

      {loading ? (
        <div className="loading-spinner">Loading proteins...</div>
      ) : (
        <div className="protein-list">
          {proteins.map((protein, index) => (
            <div key={protein.accession} className="protein-card">
              <div className="protein-header">
                <input type="checkbox" style={{ marginRight: "10px" }} />
                <strong>
                  <span
                    className="protein-link"
                    onClick={() => handleShowModal(protein, 'title')}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {protein.full_name || protein.name}
                  </span>
                </strong>
              </div>
              <span
                className="protein-index"
                onClick={() => handleShowModal(protein, 'accession')}
                style={{ cursor: 'pointer', display: 'block' }}
              >
                {(page - 1) * limit + index + 1}. <strong>Accession:</strong> {protein.accession} | <strong>Length:</strong> {protein.sequence_length} aa
              </span>
              <div className="protein-actions">
                <button className="btn-fasta" onClick={() => handleShowModal(protein, 'fasta')}>FASTA</button>
                <button className="btn-blast" onClick={() => handleBlastClick(protein)}><FaSearch /> BLAST</button>
                <a
                  href={getUniProtDetailLink(protein)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-link-btn"
                  title="View UniProt entry"
                >
                  <FaExternalLinkAlt /> UniProt
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <PaginationControls />

      {/* Modal */}
      {modalType && selectedProtein && (
        <div className="fasta-modal">
          <div className="fasta-content">
            <div className="fasta-header">
              <h3>
                {modalType === 'fasta' && `FASTA: ${selectedProtein.accession}`}
                {modalType === 'title' && `Title: ${selectedProtein.full_name || selectedProtein.name}`}
                {modalType === 'accession' && `Accession: ${selectedProtein.accession}`}
              </h3>
              <div className="fasta-actions">
                <button className="copy-btn" onClick={handleCopy}>
                  <FaCopy /> {copied ? 'Copied!' : 'Copy'}
                </button>
                {modalType === 'fasta' && (
                  <a
                    href={getUniProtDetailLink(selectedProtein)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link-btn"
                  >
                    <FaExternalLinkAlt /> UniProt
                  </a>
                )}
                <button className="close-btn" onClick={() => setModalType('')}>
                  <FaTimes />
                </button>
              </div>
            </div>
            <pre className="fasta-sequence">
              {modalType === 'fasta' && fasta}
              {modalType === 'title' && JSON.stringify(selectedProtein, null, 2)}
              {modalType === 'accession' && JSON.stringify(selectedProtein, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProteinGroupComponent;

